import { StreamingPlayerUIWidgetV2, StreamingPlayerWidgetLayout } from '@heardis/stream-contracts';
import { useEffect, useState } from 'react';
import { usePlayerContext } from '../../contexts/PlayerContext';
import { SettingsContextAnalyticsStatus, useSettingsContext } from '../../contexts/SettingsContext';
import ListIcon from '../../elements/icons/ListIcon';
import NotesIcon from '../../elements/icons/NotesIcon';
import { classNames } from '../../utils/styles';
import { Artwork, BackgroundImage } from '../Image';
import CookieContainer from '../InfoContainer/CookieContainer';
import ErrorContainer from '../InfoContainer/ErrorContainer';
import NotesContainer from '../InfoContainer/NotesContainer';
import TracklistContainer from '../InfoContainer/TracklistContainer';
import Tracklist from '../PageLayout/SidePanel/Tracklist/Tracklist';
import WidgetPlayer from '../Player/WidgetPlayer';
import { InfoContainerContent, InfoContainerState } from './widget-layout.types';
import classes from './WidgetLayout.module.css';

type WidgetLayoutProps = {
  widget: StreamingPlayerUIWidgetV2;
  widgetLayout: StreamingPlayerWidgetLayout;
};

function WidgetLayout({ widget, widgetLayout }: WidgetLayoutProps) {
  const { currentTrack } = usePlayerContext();
  const { error, analytics, setAnalytics } = useSettingsContext();
  const [infoContainerStatus, setInfoContainerStatus] = useState<InfoContainerState>({ content: null, isActive: false });
  const { player, features, typography, background } = widget;
  const { fontFamily, fontSize, fontWeight, color } = typography;
  const { image: backgroundImage, color: backgroundColor } = background;
  const { notes: notesEnabled, spotify: spotifyEnabled, tracklist: tracklistEnabled, analytics: analyticsEnabled } = features;
  const widgetLayoutIsNotSmall = widgetLayout !== StreamingPlayerWidgetLayout.SMALL;
  const largeWidgetLayout = widgetLayout === StreamingPlayerWidgetLayout.LARGE;
  const analyticsPreferencesNotSet = analytics === SettingsContextAnalyticsStatus.NOT_SET;
  let currentTrackNotes = '';
  if (currentTrack) {
    if ('notes' in currentTrack.track) {
      currentTrackNotes = currentTrack.track.notes;
    }
  }

  useEffect(() => {
    if (!analyticsEnabled) {
      setAnalytics(SettingsContextAnalyticsStatus.DISABLED);
    }
  }, []);

  const hideInfoContainer = () => {
    setInfoContainerStatus((currentInfoContainerStatus) => ({ ...currentInfoContainerStatus, isActive: false }));
  };

  useEffect(() => {
    if (analyticsPreferencesNotSet) {
      setInfoContainerStatus({ content: InfoContainerContent.COOKIE_BANNER, isActive: true });
    } else if (error) {
      setInfoContainerStatus({ content: InfoContainerContent.ERROR, isActive: true });
    } else {
      setInfoContainerStatus((currentInfoContainerStatus) => ({ ...currentInfoContainerStatus, isActive: false }));
    }
  }, [error, analyticsPreferencesNotSet]);

  const renderInfoContainerByContentStatus = () => {
    switch (infoContainerStatus.content) {
      case InfoContainerContent.COOKIE_BANNER:
        return <CookieContainer backgroundColor={backgroundColor} typography={typography} />;
      case InfoContainerContent.ERROR:
        return <ErrorContainer error={error?.code} backgroundColor={backgroundColor} typography={typography} />;
      case InfoContainerContent.NOTES:
        return <NotesContainer hideInfoContainer={hideInfoContainer} backgroundColor={backgroundColor} typography={typography} notes={currentTrackNotes} />;
      case InfoContainerContent.TRACKLIST:
        return <TracklistContainer hideInfoContainer={hideInfoContainer} backgroundColor={backgroundColor} typography={typography} notesEnabled={notesEnabled} spotifyEnabled={spotifyEnabled} />;
      default: return null;
    }
  };

  return (
    <div className={classNames(classes.widgetWrapper)} style={{ fontFamily, fontSize, fontWeight, color, backgroundColor, height: largeWidgetLayout ? '100%' : 'auto' }}>
      <div className={widgetLayoutIsNotSmall ? classes.square : ''}>
        <div className={classNames(classes.infoContainerWrapper, infoContainerStatus.isActive ? classes.active : '')}>
          {renderInfoContainerByContentStatus()}
        </div>
        {/** @TODO there should be a specific widget layout to have a proper icon scaling without conflicts with other elements */}
        {notesEnabled && currentTrackNotes && (
          <button
            type="button"
            className={classes.showNotesButton}
            style={{ color: typography.color }}
            onClick={() => setInfoContainerStatus({ content: InfoContainerContent.NOTES, isActive: true })}
          >
            <NotesIcon />
          </button>
        )}
        <WidgetPlayer
          typography={player.typography}
          elementsColor={player.elementsColor}
          backgroundColor={widgetLayoutIsNotSmall ? '' : player.background.color}
          backgroundImage={widgetLayoutIsNotSmall ? { ...player.background.image, url: '' } : player.background.image}
          artwork={{ ...player.artwork, enabled: false }}
          spotifyEnabled={features.spotify}
          controlFeatureRight={tracklistEnabled && !largeWidgetLayout && (
            <button
              type="button"
              onClick={() => setInfoContainerStatus({ content: InfoContainerContent.TRACKLIST, isActive: true })}
            >
              <ListIcon fill={player.elementsColor} />
            </button>
          )}
        />
        {widgetLayoutIsNotSmall && (
          <>
            <div className={classes.colorGradientOverlay} style={{ background: `linear-gradient(rgba(0, 0, 0, 0), ${player.background.color} 60%)` }} />
            <div className={classes.absoluteArtworkWrapper}>
              <Artwork
                alwaysDefaultEnabled={player.artwork.alwaysDefault}
                defaultArtwork={player.artwork.defaultImage}
                track={currentTrack?.track}
              />
            </div>
          </>
        )}
      </div>
      {largeWidgetLayout && tracklistEnabled && (
        <Tracklist notesEnabled={notesEnabled} spotifyEnabled={spotifyEnabled} />
      )}
      {backgroundImage.url && (
        <BackgroundImage
          url={backgroundImage.url}
          alt={backgroundImage.alt}
          blur={backgroundImage.blur}
          position={backgroundImage.position}
          fit={backgroundImage.fit}
          zIndex={-20}
        />
      )}
    </div>
  );
}

export default WidgetLayout;
